import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useChallengesState } from 'hooks/useChallengesState';

const CategorySelector = styled.div.attrs({
  className: 'CategorySelector'
})`
  position: relative;
  display: flex;
  flex: 1;
  max-width: 140px;
`;

const CategoryDropdown = styled.div.attrs({
  className: 'CategoryDropdown'
})`
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 8px 12px;
  background: #2e2e2e;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
  box-sizing: border-box;
  flex-wrap: nowrap;
  text-wrap: nowrap;
  min-width: 140px;
  max-width: 140px;
  justify-content: flex-start;
  max-height: 33px;
  flex: 1;
  &:hover {
    background: #333;
  }
`;

const IconWrapper = styled.span.attrs({
  className: 'IconWrapper'
})`
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
  font-size: 12px;
`;

const DropdownArrow = styled.div.attrs({
  className: 'DropdownArrow'
})`  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg) translateY(2px)' : 'rotate(0deg) translateY(2px)'};
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  margin-left: auto;
  svg {
    font-size: 18px;
  }
`;

const CategoriesList = styled.div.attrs({
  className: 'CategoriesList'
})`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 3px;
  overflow: hidden;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transform: translateY(-10px);
  visibility: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  ${props => props.$isOpen && `
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
  `}
`;

const CategoryItem = styled.div.attrs({
  className: 'CategoryItem'
})`
  padding: 10px 12px;
  color: black;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: #f5f5f5;
  }
`;

const SelectCategory = ({ challenge }) => {
  const { state, attachCategory, detachCategory } = useChallengesState();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const activeCategory = state.challenges.categories[challenge?.category_id];
  // Filter out the uncategorized option and only show actual categories
  const categories = Object.values(state.challenges.categories)
    .filter(category => category.id !== 'uncategorized');

  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (category_id) => {
    // Don't do anything if selecting the same category
    if (challenge.category_id === category_id) {
      setIsOpen(false);
      return;
    }

    // Just attach to new category - the reducer will handle moving from old location
    attachCategory(category_id, challenge.id);
    setIsOpen(false);
  };

  return (
    <CategorySelector ref={containerRef}>
      <CategoryDropdown onClick={handleToggle}>
        {activeCategory && activeCategory.id !== 'uncategorized' ? (
          <>
            <IconWrapper>{activeCategory.icon}</IconWrapper>
            {activeCategory.name}
          </>
        ) : (
          "Select Category"
        )}
        <DropdownArrow $isOpen={isOpen}>
          <KeyboardArrowDownIcon />
        </DropdownArrow>
      </CategoryDropdown>

      <CategoriesList $isOpen={isOpen}>
        {categories.map((category) => (
          <CategoryItem
            key={category.id}
            onClick={() => handleSelect(category.id)}
          >
            <IconWrapper>{category.icon}</IconWrapper>
            {category.name}
          </CategoryItem>
        ))}
      </CategoriesList>
    </CategorySelector>
  );
};

export default SelectCategory;